import React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { popup, popdown } from 'modules/helpers/animations';
import breakpoint from 'modules/breakpoint';
import useIsomorphicLayoutEffect from 'hooks/globals/useIsomorphicLayoutEffect';
import DefaultHeader from '../components/header';
import CloseButton from '../components/closeButton';

const animationDuration = 300;
let popupAnimationHasRun = false;

export const DefaultBox = styled.div<{ open: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => (theme as any).backgroundColor || '#fff'};
    width: 100%;
    height: ${({ theme }) => ((theme as any).placement === 'center' ? 'auto' : '100%')};
    min-height: ${({ theme }) => (theme as any).minHeight || 'var(--modal-min-height)'};
    max-height: ${({ theme }) => (theme as any).maxHeight || '100%'};
    padding: ${({ theme }) => (theme as any).padding || '0'};
    animation: ${({ open }) => (open ? !popupAnimationHasRun && popup : popdown)} ${animationDuration}ms;
    animation-fill-mode: both;
    animation-timing-function: cubic-bezier(0.29, 0.97, 0.46, 1);
    overflow: auto;
    overflow-x: hidden;

    ${({ theme }) =>
        (theme as any).placement === 'top' &&
        css`
            align-self: flex-start;
            height: auto;
        `};

    ${({ theme }) =>
        (theme as any).placement === 'bottom' &&
        css`
            align-self: flex-end;
            height: auto;
        `};

    @media (min-width: ${breakpoint.sizes.md}px) {
        width: ${({ theme }) => (theme as any).width || 'auto'};
        height: ${({ theme }) => (theme as any).height || 'auto'};
        min-width: ${({ theme }) => (theme as any).minWidth || '600px'};
        max-width: ${({ theme }) => (theme as any).maxWidth || '90%'};
        max-height: ${({ theme }) => (theme as any).maxHeight || '90%'};

        ${({ theme }) => (theme as any).placement && 'align-self: auto;'}
    }
`;

const ContentWrapper = styled.div`
    flex: 1 1 auto;
    height: inherit; /* iOS fix, auto doesn't work */
    display: flex;
    flex-direction: column;
    -webkit-overflow-scrolling: touch;
    overflow-x: ${({ theme }) => (theme as any).overflowX || 'auto'};
    overflow-y: ${({ theme }) => (theme as any).overflowY || 'auto'};
`;

interface ITheme {
    color?: string;
    fill?: string;
    width?: string;
    height?: string;
    minWidth?: string;
    minHeight?: string;
    maxWidth?: string;
    maxHeight?: string;
    backgroundColor?: string;
    placement?: string;
    overflowX?: string;
    overflowY?: string;
    padding?: string;
}

export interface IDefaultLayout {
    component?: (props) => JSX.Element;
    customTheme?: ITheme;
    header?: any;
    className?: any;
    open?: any;
    close?: any;
    userCanClose?: any;
    unmount?: any;
    content?: any;
    lockScroll?: any;
    Box?: any;
    trackingCategory?: any;
    theme?: any;
}

export default (props: IDefaultLayout) => {
    const {
        customTheme = {},
        header,
        className,
        open,
        close,
        userCanClose,
        unmount,
        content,
        Box = DefaultBox,
        trackingCategory
    } = props;
    const scrollRef = React.createRef<any>();

    // Only run this effect if props.open change
    useIsomorphicLayoutEffect(() => {
        if (open) {
            popupAnimationHasRun = true;
        } else {
            unmount(animationDuration);
            popupAnimationHasRun = false;
        }
    }, [open, scrollRef, unmount]);

    const HeaderProp = (headerProps) => (header !== null ? React.cloneElement(header, headerProps) : null);

    const closeClassName = trackingCategory ? `tracking_${trackingCategory}_closeModal` : null;

    return (
        <ThemeProvider theme={customTheme as any}>
            <Box className={className} open={open}>
                {header !== undefined ? (
                    <HeaderProp userCanClose={userCanClose} close={close} />
                ) : (
                    <DefaultHeader>
                        {userCanClose && <CloseButton onClick={close} className={closeClassName} />}
                    </DefaultHeader>
                )}
                <ContentWrapper ref={scrollRef}>{content}</ContentWrapper>
            </Box>
        </ThemeProvider>
    );
};
